import React, { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import styled from "styled-components";

import { messageConstants, generalConstants } from "_constants";
import { messageAction } from "actions";
import _ from "lodash";
import moment from "moment";

import ProgressiveRejectDebouncer from "_util/debouncers/ProgressiveRejectDebouncer";
import { history, getMsgStatus } from "_util/_helper";

import { Status, Pagination, RefreshButton } from "components/general";
import { StandardSelect, StandardTextField } from "components/general/standard";
import { ArrowDropDown, ArrowDropUp, Done } from "@material-ui/icons";
import { ReactComponent as OrganizationIcon } from "resources/img/organization.svg";

import { AdminMenubar, AdminUserInfo, AdminSectionbar, AdminFooter } from "components/admin";
import {
	PageContainer,
	LeftContainer,
	RightContainer,
	ContentContainer,
	SearchBar,
	SearchBarLeft,
	SearchBarRight,
	PaginationContainer,
	TableContainer,
	TableRow,
	TableTh,
	THButton,
	TableTd,
} from "components/layout";

import { ReactComponent as NewMsgIcon } from "resources/img/newmsg.svg";

const DEFAULT_PAGE = 1;
const DEFAULT_LIMIT = 10;
const FROM_MSG = "message";
export const MsgManagementPage = () => {
	const { msgList, currentPage, totalItems, numOfPage, messageSearchKey, messageSearchWord, messageSortingKey, messageSortingDirection } =
		useSelector(({ message }) => ({
			msgList: message.navigationObject?.root?.msgList,
			currentPage: message.navigationObject?.root?.currentPage,
			totalItems: message.navigationObject?.root?.totalItems,
			numOfPage: message.navigationObject?.root?.numOfPage,
			messageSearchKey: message.navigationObject?.root?.searchKey,
			messageSearchWord: message.navigationObject?.root?.searchWord,
			messageSortingKey: message.navigationObject?.root?.sortingKey,
			messageSortingDirection: message.navigationObject?.root?.sortingDirection,
		}));

	const [searchKey, setSearchKey] = useState(messageSearchKey || messageConstants.MSG_FILTER.SCHOOL_NAME);
	const [searchWord, setSearchWord] = useState(messageSearchWord || "");
	const [sortingKey, setSortingKey] = useState(messageSortingKey || "lastUpdateTime");
	const [sortingDirection, setSortingDirection] = useState(messageSortingDirection || "DESC");

	const dispatch = useDispatch();
	const timeoutRef = useRef(null);
	const [textDebouncer] = useState(() => new ProgressiveRejectDebouncer(0.7 * 1000));
	const [debouncer] = useState(() => new ProgressiveRejectDebouncer(1 * 1000));

	useEffect(() => {
		let form = {
			page: currentPage,
			limit: DEFAULT_LIMIT,
			sort: `${sortingKey}:${sortingDirection}`,
			includeSchool: true,
		};
		if (searchKey === messageConstants.MSG_FILTER.SCHOOL_NAME && searchWord) {
			form = { ...form, school: { name: searchWord } };
		} else if (searchKey === messageConstants.MSG_FILTER.TITLE && searchWord) {
			form = { ...form, msg: { title: searchWord } };
		}
		dispatch(messageAction.fetchMessages(form, "root"));
	}, []);

	const handleSorting = (key) => {
		if (!debouncer.token()) {
			return;
		}
		let direction = "ASC";
		if (sortingKey === key && sortingDirection === "ASC") {
			direction = "DESC";
		}
		setSortingKey(key);
		setSortingDirection(direction);
		let form = {
			page: DEFAULT_PAGE,
			limit: DEFAULT_LIMIT,
			sort: `${key}:${direction}`,
			includeSchool: true,
		};
		if (searchKey === messageConstants.MSG_FILTER.SCHOOL_NAME && searchWord) {
			form = { ...form, school: { name: searchWord } };
		} else if (searchKey === messageConstants.MSG_FILTER.TITLE && searchWord) {
			form = { ...form, msg: { title: searchWord } };
		}
		dispatch(messageAction.fetchMessages(form, "root"));
	};

	const handlePageChange = (pageNum) => {
		if (!debouncer.token() || currentPage === pageNum) {
			return;
		}
		let form = {
			page: pageNum,
			limit: DEFAULT_LIMIT,
			sort: `${sortingKey}:${sortingDirection}`,
			includeSchool: true,
		};
		if (searchKey === messageConstants.MSG_FILTER.SCHOOL_NAME && searchWord) {
			form = { ...form, school: { name: searchWord } };
		} else if (searchKey === messageConstants.MSG_FILTER.TITLE && searchWord) {
			form = { ...form, msg: { title: searchWord } };
		}
		dispatch(messageAction.fetchMessages(form, "root"));
	};

	function resetTimeout() {
		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current);
		}
	}
	const handleChange = ({ key, value }) => {
		const tempSearchKey = key === "searchKey" ? value : searchKey;
		const tempSearchWord = key === "searchWord" ? value : searchWord;
		if (key === "searchKey") {
			setSearchKey(value);
		}
		if (key === "searchWord") {
			setSearchWord(value);
		}
		if (key === "searchKey" && _.isEmpty(tempSearchWord)) {
			return;
		}
		let form = {
			page: DEFAULT_PAGE,
			limit: DEFAULT_LIMIT,
			sort: `${sortingKey}:${sortingDirection}`,
			includeSchool: true,
		};
		if (tempSearchKey === messageConstants.MSG_FILTER.SCHOOL_NAME && tempSearchWord) {
			form = { ...form, school: { name: tempSearchWord } };
		} else if (tempSearchKey === messageConstants.MSG_FILTER.TITLE && tempSearchWord) {
			form = { ...form, msg: { title: tempSearchWord } };
		}
		if (!textDebouncer.token()) {
			resetTimeout();
			timeoutRef.current = setTimeout(() => {
				dispatch(messageAction.fetchMessages(form, "root"));
			}, 1000);
			return;
		}
		resetTimeout();
		dispatch(messageAction.fetchMessages(form, "root"));
	};

	const handleRefreshFilter = () => {
		if (searchWord === "" && sortingKey === "lastUpdateTime" && sortingDirection === "DESC" && currentPage === DEFAULT_PAGE) {
			setSearchKey(messageConstants.MSG_FILTER.SCHOOL_NAME);
			return;
		}
		setSearchKey(messageConstants.MSG_FILTER.SCHOOL_NAME);
		setSearchWord("");
		setSortingKey("lastUpdateTime");
		setSortingDirection("DESC");
		let form = {
			page: DEFAULT_PAGE,
			limit: DEFAULT_LIMIT,
			sort: `lastUpdateTime:DESC`,
			includeSchool: true,
		};
		dispatch(messageAction.fetchMessages(form, "root"));
	};

	const renderTH = () => {
		return messageConstants.TABLE_COLUMN.map((column) => {
			return (
				<TableTh key={`${column.key}_table_header`} width={column.width}>
					<THButton key={`${column.key}_table_header`} type="button" onClick={() => handleSorting(column.key)}>
						<div style={{ textAlign: column.key === "status" ? "center" : "auto" }}>
							{column.value}
							{sortingKey === column.key ? (
								sortingDirection === "ASC" ? (
									<ArrowDropUp fontSize={"small"} style={{ verticalAlign: "middle" }} />
								) : (
									<ArrowDropDown fontSize={"small"} style={{ verticalAlign: "middle" }} />
								)
							) : null}
						</div>
					</THButton>
				</TableTh>
			);
		});
	};
	const renderTableHeader = renderTH();

	const handleNewMsg = () => {
		history.push({ pathname: generalConstants.PATH.NEW_MSG, state: { from: FROM_MSG } });
	};

	const HandleRowOnClick = (item) => {
		history.push({
			pathname: generalConstants.PATH.EDIT_MSG,
			state: {
				from: FROM_MSG,
				msgKey: item._key,
				school: item.school,
			},
		});
	};

	const renderTD = () => {
		if (msgList?.length > 0) {
			return msgList?.map((msg, index) => {
				const tempObj = {};
				messageConstants.TABLE_COLUMN.forEach((column) => {
					tempObj[column.key] = msg[column.key];
				});
				return (
					<TableRow
						key={`${msg._id}_${index}_tr`}
						onClick={() => {
							HandleRowOnClick(msg);
						}}
					>
						{Object.keys(tempObj).map((columnName, index) => {
							if (columnName === "status") {
								return (
									<TableTd key={`${msg._id}_${columnName}_${index}_td`} style={{ verticalAlign: "middle" }}>
										<Status status={getMsgStatus(msg)} />
									</TableTd>
								);
							} else if (columnName === "type" || columnName === "infoType" || columnName === "targetAudience") {
								return (
									<TableTd key={`${msg._id}_${columnName}_${index}_td`}>
										{messageConstants.Translate[columnName][tempObj[columnName]]}
									</TableTd>
								);
							} else if (columnName === "title") {
								return (
									<TableTd style={{ display: "flex", alignItems: "center" }} key={`${msg._id}_${columnName}_${index}_td`}>
										<TitleText>{tempObj[columnName]}</TitleText>
										{(msg.lastUpdateBy?.startsWith("organization/") || msg?.createdBy?.startsWith("organization/")) && (
											<OrganizationIcon style={{ marginLeft: 5, marginRight: 5, width: 15, height: 19 }} fill={"#333399"} />
										)}
									</TableTd>
								);
							} else if (columnName === "scheduleTime") {
								return (
									<TableTd key={`${msg._id}_${columnName}_${index}_td`}>
										{tempObj[columnName] ? moment(tempObj[columnName]).format("YYYY年M月DD日 hh:mmA") : "-"}
									</TableTd>
								);
							} else if (columnName === "fired") {
								return <TableTd key={`${msg._id}_${columnName}_${index}_td`}>{tempObj[columnName] ? <Done /> : null}</TableTd>;
							} else if (columnName === "schoolName") {
								return (
									<TableTd key={`${msg._id}_${columnName}_${index}_td`}>
										<OverviewContainer>
											{msg?.school?.vipOnly && (
												<VipIconView>
													<VipText>VIP</VipText>
												</VipIconView>
											)}
											{tempObj[columnName]}
										</OverviewContainer>
									</TableTd>
								);
							}
							return <TableTd key={`${msg._id}_${columnName}_${index}_td`}>{tempObj[columnName]}</TableTd>;
						})}
					</TableRow>
				);
			});
		}
	};
	const renderTableCell = renderTD();

	return (
		<>
			<PageContainer>
				<LeftContainer>
					<AdminMenubar section={generalConstants.NAV_TAB.MSG_MANAGEMENT.key} />
				</LeftContainer>
				<RightContainer>
					<AdminUserInfo />
					<AdminSectionbar
						title={generalConstants.NAV_TAB.MSG_MANAGEMENT.key}
						buttonLabel={"發送新訊息"}
						buttonIcon={<NewMsgIcon />}
						handleOnClick={handleNewMsg}
						sublabel={"訊息數量: " + totalItems}
						locationPathArray={[
							{ title: "主頁面", isAction: true, target: generalConstants.PATH.DASHBOARD },
							{
								title: generalConstants.TAB_NAME[generalConstants.NAV_TAB.MSG_MANAGEMENT.key],
								isAction: false,
								target: "",
							},
						]}
					/>
					<ContentContainer>
						<SearchBar>
							<SearchBarLeft>
								<RowInBlock marginBottom="0px">
									<div style={{ marginLeft: "10px", width: "20%" }}>
										<StandardSelect
											name={"searchKey"}
											options={_.filter(messageConstants.TABLE_COLUMN, { searchable: true })}
											value={searchKey}
											handleChange={handleChange}
										/>
									</div>
									<div style={{ marginLeft: "10px", width: "40%" }}>
										<StandardTextField
											placeholder="關鍵字"
											value={searchWord}
											name="searchWord"
											handleChange={handleChange}
											style={{ borderTopRightRadius: "0px", borderBottomRightRadius: "0px" }}
										/>
									</div>
									<RefreshButton style={{ marginLeft: "5px" }} handleOnClick={handleRefreshFilter} label="重置選項"></RefreshButton>
								</RowInBlock>
							</SearchBarLeft>
							<SearchBarRight>
								<RowInBlock marginBottom="0px" style={{ float: "right" }}></RowInBlock>
							</SearchBarRight>
						</SearchBar>
						<TableContainer>
							<table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
								<thead>
									<tr>{renderTableHeader}</tr>
								</thead>
								<tbody>{renderTableCell}</tbody>
							</table>
						</TableContainer>
						<PaginationContainer>
							<Pagination count={numOfPage} page={currentPage} handlePageChange={handlePageChange} />
						</PaginationContainer>
					</ContentContainer>
					<AdminFooter></AdminFooter>
				</RightContainer>
			</PageContainer>
		</>
	);
};

const TitleText = styled.span`
	color: #0085b5;
	text-decoration: underline;
`;

const RowInBlock = styled.div`
	display: flex;
	margin-bottom: ${(props) => props.marginBottom || "10px"};
	flex-direction: row;
`;

const OverviewContainer = styled.div`
	display: flex;
	flex-direction: row;
	align-items: center;
`;

const VipIconView = styled.div`
	display: flex;
	width: 32px;
	flex-direction: row;
	align-items: center;
	justify-content: center;
	margin-right: 5px;
`;
const VipText = styled.div`
	font-size: 12;
	font-weight: bold;
	color: #333333;
	background-color: #c8ae63;
	padding-left: 5px;
	padding-right: 5px;
`;
